import React, { useEffect, useMemo } from "react";
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ContentLayout,
} from "@cloudscape-design/components";
import { useNavigate } from "react-router-dom";
import { useUserAlias } from "../../hooks/useUserAlias";
import { useGlobalStore } from "../../store/global-store";
import { useWorkflowStore } from "../../store/workflow-store";
import { ROUTES } from "../../constants";
import { default as FinSpaceCard } from "../common/Card/Card";
import HorizontalCardContainer from "../common/HorizontalCardContainer";
import SectionHeader from "../common/SectionHeader";
import Button from "../common/Button";
import ReportAnalysisCard from "./ReportAnalysisCard";
import RecentActivityTable from "./RecentActivityTable";
import { useGetAllWorkflowExecutions } from "../../api/hooks";
import { transformToActivityItem } from "../../utils/activityTransformUtils";
import { transformWorkflowTemplate } from "../../utils/workflowHelpers";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { alias } = useUserAlias();
  const { selectedSpaceId, fetchSpaces } = useGlobalStore();
  const { workflowTemplates, fetchWorkflowTemplates, workflows, fetchWorkflows } = useWorkflowStore();
  const { data: executionsData, isLoading: isExecutionsLoading } = useGetAllWorkflowExecutions(selectedSpaceId || undefined);

  // Fetch spaces and workflow templates on mount
  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    if (selectedSpaceId) {
      fetchWorkflowTemplates(selectedSpaceId);
      fetchWorkflows(selectedSpaceId);
    }
  }, [selectedSpaceId, fetchWorkflowTemplates, fetchWorkflows]);

  // Transform API data to recent activity items (latest 5 based on creation time)
  const recentActivityItems = useMemo(() => {
    if (!executionsData?.results || !workflows) {
      return [];
    }

    try {
      return executionsData.results
        .map(execution => {
          const workflow = workflows.find(w => w.id === execution.workflowId);
          return transformToActivityItem(execution, workflow);
        })
        .filter(item => item.createdAt) // Only include items with creation time
        .sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()) // Sort by creation time desc
        .slice(0, 5); // Take latest 5
    } catch (err) {
      console.error('Failed to transform workflow executions for recent activity:', err);
      return [];
    }
  }, [executionsData, workflows]);

  // Use API data - sort alphabetically by name
  const reportTypes = workflowTemplates
    ? workflowTemplates
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map(transformWorkflowTemplate)
    : [];

  // Add navigation to workflow actions
  const enhancedReportTypes = reportTypes.map(type => ({
    ...type,
    workflowActions: type.workflowActions.map(action => ({
      ...action,
      onClick: () => navigate(action.route)
    }))
  }));

  return (
    <ContentLayout
      defaultPadding
      header={<Header variant="h1">Hello {alias || 'There'}!</Header>}
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Report/Analysis Types Section */}
        <Box>
          <SectionHeader
            title="Report/Analysis Types"
            variant="h2"
            // Disabling the View All button for now
            // actions={
            //   <Button
            //     variant="secondary"
            //     icon="angle-right"
            //     iconPosition="trailing"
            //     onClick={() => {
            //       navigate(ROUTES.REPORTS_ALL);
            //     }}
            //   >
            //     View all
            //   </Button>
            // }
          />
          <HorizontalCardContainer ariaLabel="Featured cards horizontal scroll">
            {enhancedReportTypes.map((type, index) => (
              <FinSpaceCard key={index} disabled={type.disabled}>
                <ReportAnalysisCard
                  title={type.workflowName}
                  description={type.workflowDescription}
                  avatar={{
                    type: "initials",
                    content: type.workflowShortName,
                    variant: "primary",
                  }}
                  actions={type.workflowActions}
                />
              </FinSpaceCard>
            ))}
          </HorizontalCardContainer>
        </Box>

        {/* Recent Activity Section */}
        <Box>
          <SectionHeader
            title="Recent Activity"
            variant="h2"
            actions={
              <Button
                variant="secondary"
                icon="angle-right"
                iconPosition="trailing"
                onClick={() => {
                  navigate(ROUTES.ACTIVITIES_ALL);
                }}
              >
                View all
              </Button>
            }
          />
          <Container>
            <RecentActivityTable items={recentActivityItems} loading={isExecutionsLoading} />
          </Container>
        </Box>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default Dashboard;
